/**
 * Google OAuth Authentication Form
 */
import React, { useState } from 'react';

const GoogleAuthForm = ({ onSuccess, onCancel }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);

  const handleGoogleAuth = async () => {
    setIsAuthenticating(true);
    setAuthError(null);
    
    // 🚨 SAFETY: Auto-reset authenticating state after 10 seconds to prevent stuck state
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ Google auth timeout - resetting authenticating state');
      setIsAuthenticating(false);
      setAuthError('Authentication timed out. Please try again.');
    }, 10000);
    
    try {
      // Check if Google Identity Services is loaded
      if (!window.google || !window.google.accounts) {
        throw new Error('Google OAuth SDK not loaded. Please check your internet connection.');
      }

      // Initialize Google OAuth
      window.google.accounts.id.initialize({
        client_id: '896517772559-r3n8p2bst6pf8c8v2u8c8t0h4e4g0p8l.apps.googleusercontent.com',
        callback: (response) => {
          clearTimeout(timeoutId);
          try {
            const payload = JSON.parse(atob(response.credential.split('.')[1]));
            
            const profile = {
              name: payload.name,
              email: payload.email,
              avatar: payload.picture,
              type: 'google',
              provider_id: payload.sub
            };
            
            onSuccess(profile);
            setIsAuthenticating(false);
          } catch (error) {
            console.error('❌ Error processing Google OAuth response:', error);
            setAuthError('Failed to process authentication response');
            setIsAuthenticating(false);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.prompt();
      
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('❌ Google OAuth error:', error);
      setAuthError(error.message || 'Google authentication failed');
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="text-center py-6">
      <div className="mb-6">
        <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">🔍</span>
        </div>
        <h4 className="text-white text-lg font-semibold mb-2">Sign in with Google</h4>
        <p className="text-white/70 text-sm">Use your Google account for quick setup</p>
      </div>

      {authError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-start">
            <p className="text-red-400 text-sm flex-1">❌ {authError}</p>
            <button
              onClick={() => {
                setAuthError(null);
                setIsAuthenticating(false);
              }}
              className="ml-2 text-red-400 hover:text-red-300 text-xs underline"
              title="Clear error and reset"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <button
          onClick={handleGoogleAuth}
          disabled={isAuthenticating}
          className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
        >
          {isAuthenticating ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Connecting to Google...</span>
            </div>
          ) : (
            <>🔍 Continue with Google</>
          )}
        </button>
        
        <button
          onClick={onCancel}
          className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
        >
          ← Back to Quick Setup
        </button>
      </div>
    </div>
  );
};

export default GoogleAuthForm;
